import { createRouter, createWebHistory, RouteMeta, RouteRecordRaw } from 'vue-router'

import AuthLayout from '../layouts/AuthLayout.vue'
import AppLayout from '../layouts/AppLayout.vue'

import RouteViewComponent from '../layouts/RouterBypass.vue'
import { can, hasPermissions, refreshAccess } from '../services/access'

// meta.permission: the page opens when the account holds any of these keys (touch-api/docs/rbac.md).
// Pages without it are open to every signed-in account.
declare module 'vue-router' {
  interface RouteMeta {
    permission?: string[]
  }
}
const COURSES = ['courses.view', 'courses.view.own', 'courses.view.assigned']
const USERS = ['users.view', 'users.view.own']
const B2B_VIEW = ['b2b.view', 'b2b.view.assigned']

// Where an account lands after login / when a page is not allowed: the first one it may open
const HOME_ROUTES = [
  'dashboard',
  'commerce-orders',
  'warehouse-overview',
  'b2b-overview',
  'social-calendar',
  'affiliate-me',
  'settings',
]

const routes: Array<RouteRecordRaw> = [
  {
    // Customer preview link (no account): outside AppLayout, public in the guard below
    name: 'social-public-preview',
    path: '/preview/post/:token',
    component: () => import('../pages/social/PublicPreviewPage.vue'),
  },
  {
    path: '/:pathMatch(.*)*',
    redirect: { name: 'dashboard' },
  },
  {
    name: 'admin',
    path: '/',
    component: AppLayout,
    redirect: { name: 'dashboard' },
    children: [
      {
        name: 'dashboard',
        meta: { permission: ['training.dashboard.view', ...COURSES] },
        path: 'dashboard',
        component: () => import('../pages/admin/dashboard/Dashboard.vue'),
      },
      {
        name: 'settings',
        path: 'settings',
        component: () => import('../pages/settings/Settings.vue'),
      },
      {
        name: 'preferences',
        path: 'preferences',
        component: () => import('../pages/preferences/Preferences.vue'),
      },
      {
        name: 'users',
        meta: { permission: USERS },
        path: 'users',
        component: () => import('../pages/users/UsersPage.vue'),
      },
      {
        name: 'projects',
        meta: { permission: ['access.manage'] },
        path: 'projects',
        component: () => import('../pages/projects/ProjectsPage.vue'),
      },
      {
        name: 'courses',
        meta: { permission: COURSES },
        path: 'courses',
        component: () => import('../pages/courses/CoursesPage.vue'),
      },
      {
        name: 'payments',
        meta: { permission: ['access.manage'] },
        path: '/payments',
        component: RouteViewComponent,
        children: [
          {
            name: 'payment-methods',
            path: 'payment-methods',
            component: () => import('../pages/payments/PaymentsPage.vue'),
          },
          {
            name: 'billing',
            path: 'billing',
            component: () => import('../pages/billing/BillingPage.vue'),
          },
          {
            name: 'pricing-plans',
            path: 'pricing-plans',
            component: () => import('../pages/pricing-plans/PricingPlans.vue'),
          },
        ],
      },
      {
        name: 'social',
        meta: { permission: ['social.access'] },
        path: '/social',
        component: RouteViewComponent,
        redirect: { name: 'social-calendar' },
        children: [
          {
            name: 'social-calendar',
            path: 'calendar',
            component: () => import('../pages/social/SocialCalendarPage.vue'),
          },
          {
            name: 'social-posts',
            path: 'posts',
            component: () => import('../pages/social/SocialPostsPage.vue'),
          },
          {
            name: 'social-channels',
            path: 'channels',
            component: () => import('../pages/social/SocialChannelsPage.vue'),
          },
          {
            name: 'social-analytics',
            path: 'analytics',
            component: () => import('../pages/social/SocialAnalyticsPage.vue'),
          },
          {
            name: 'social-analytics-channel',
            path: 'analytics/channels/:id',
            component: () => import('../pages/social/SocialChannelAnalyticsPage.vue'),
          },
          {
            // OAuth redirect URI registered on Facebook / Instagram / Google / TikTok apps
            name: 'social-callback',
            path: 'callback/:platform',
            component: () => import('../pages/social/SocialCallbackPage.vue'),
          },
        ],
      },
      {
        name: 'commerce',
        path: '/commerce',
        component: RouteViewComponent,
        redirect: { name: 'commerce-orders' },
        children: [
          {
            name: 'commerce-orders',
            meta: { permission: ['commerce.orders.view'] },
            path: 'orders',
            component: () => import('../pages/commerce/OrdersPage.vue'),
          },
          {
            name: 'commerce-customers',
            meta: { permission: ['commerce.customers.view'] },
            path: 'customers',
            component: () => import('../pages/commerce/CustomersPage.vue'),
          },
          {
            name: 'commerce-products',
            meta: { permission: ['commerce.catalog.view'] },
            path: 'products',
            component: () => import('../pages/commerce/ProductsPage.vue'),
          },
          {
            name: 'commerce-categories',
            meta: { permission: ['commerce.catalog.manage'] },
            path: 'categories',
            component: () => import('../pages/commerce/CategoriesPage.vue'),
          },
          {
            name: 'commerce-articles',
            meta: { permission: ['commerce.articles.view'] },
            path: 'articles',
            component: () => import('../pages/commerce/ArticlesPage.vue'),
          },
          {
            name: 'commerce-promotions',
            meta: { permission: ['commerce.promotions.view'] },
            path: 'promotions',
            component: () => import('../pages/commerce/PromotionsPage.vue'),
          },
          {
            name: 'commerce-channels',
            meta: { permission: ['commerce.channels.view'] },
            path: 'channels',
            component: () => import('../pages/channels/ChannelsPage.vue'),
          },
          {
            name: 'commerce-channel',
            meta: { permission: ['commerce.channels.view'] },
            path: 'channels/:id',
            component: () => import('../pages/channels/ChannelDetailPage.vue'),
          },
          {
            // moved to the warehouse module
            name: 'commerce-inventory',
            path: 'inventory',
            redirect: { name: 'warehouse-stock' },
          },
          {
            name: 'commerce-reports',
            meta: { permission: ['commerce.reports.view'] },
            path: 'reports',
            component: () => import('../pages/commerce/SalesReportPage.vue'),
          },
        ],
      },
      {
        name: 'warehouse',
        path: '/warehouse',
        component: RouteViewComponent,
        redirect: { name: 'warehouse-overview' },
        children: [
          {
            name: 'warehouse-overview',
            meta: { permission: ['warehouse.view'] },
            path: 'overview',
            component: () => import('../pages/warehouse/OverviewPage.vue'),
          },
          {
            name: 'warehouse-outbound',
            meta: { permission: ['warehouse.outbound.manage'] },
            path: 'outbound',
            component: () => import('../pages/warehouse/OutboundPage.vue'),
          },
          {
            name: 'warehouse-returns',
            meta: { permission: ['warehouse.returns.view'] },
            path: 'returns',
            component: () => import('../pages/warehouse/ReturnsPage.vue'),
          },
          {
            name: 'warehouse-stock',
            meta: { permission: ['warehouse.view'] },
            path: 'stock',
            component: () => import('../pages/warehouse/StockPage.vue'),
          },
          {
            name: 'warehouse-documents',
            meta: { permission: ['warehouse.documents.view'] },
            path: 'documents',
            component: () => import('../pages/warehouse/DocumentsPage.vue'),
          },
          {
            name: 'warehouse-stocktakes',
            meta: { permission: ['warehouse.stocktakes.manage'] },
            path: 'stocktakes',
            component: () => import('../pages/warehouse/StocktakesPage.vue'),
          },
          {
            name: 'warehouse-stocktake',
            meta: { permission: ['warehouse.stocktakes.manage'] },
            path: 'stocktakes/:id',
            component: () => import('../pages/warehouse/StocktakeDetailPage.vue'),
          },
          {
            name: 'warehouse-allocations',
            meta: { permission: ['warehouse.allocations.view'] },
            path: 'allocations',
            component: () => import('../pages/warehouse/AllocationsPage.vue'),
          },
          {
            name: 'warehouse-reports',
            meta: { permission: ['warehouse.reports.view', 'warehouse.valuation.view'] },
            path: 'reports',
            component: () => import('../pages/warehouse/ReportsPage.vue'),
          },
          {
            name: 'warehouse-setup',
            meta: { permission: ['warehouse.setup.manage', 'warehouse.suppliers.manage'] },
            path: 'setup',
            component: () => import('../pages/warehouse/SetupPage.vue'),
          },
        ],
      },
      {
        name: 'b2b',
        path: '/b2b',
        component: RouteViewComponent,
        redirect: { name: 'b2b-dealers' },
        children: [
          {
            name: 'b2b-overview',
            meta: { permission: ['b2b.reports.view'] },
            path: 'overview',
            component: () => import('../pages/b2b/OverviewPage.vue'),
          },
          {
            name: 'b2b-dealers',
            meta: { permission: B2B_VIEW },
            path: 'dealers',
            component: () => import('../pages/b2b/DealersPage.vue'),
          },
          {
            name: 'b2b-dealer',
            meta: { permission: B2B_VIEW },
            path: 'dealers/:id',
            component: () => import('../pages/b2b/DealerDetailPage.vue'),
          },
          {
            name: 'b2b-orders',
            meta: { permission: B2B_VIEW },
            path: 'orders',
            component: () => import('../pages/b2b/OrdersPage.vue'),
          },
          {
            name: 'b2b-receipts',
            meta: { permission: B2B_VIEW },
            path: 'receipts',
            component: () => import('../pages/b2b/ReceiptsPage.vue'),
          },
          {
            name: 'b2b-aging',
            meta: { permission: ['b2b.reports.view'] },
            path: 'aging',
            component: () => import('../pages/b2b/AgingPage.vue'),
          },
          {
            name: 'b2b-tiers',
            meta: { permission: ['b2b.pricing.manage'] },
            path: 'tiers',
            component: () => import('../pages/b2b/TiersPage.vue'),
          },
        ],
      },
      {
        name: 'affiliate',
        path: '/affiliate',
        component: RouteViewComponent,
        redirect: { name: 'affiliate-list' },
        children: [
          {
            name: 'affiliate-list',
            meta: { permission: ['affiliate.manage'] },
            path: 'affiliates',
            component: () => import('../pages/affiliate/AffiliatesPage.vue'),
          },
          {
            name: 'affiliate-detail',
            meta: { permission: ['affiliate.manage'] },
            path: 'affiliates/:id',
            component: () => import('../pages/affiliate/AffiliateDetailPage.vue'),
          },
          {
            name: 'affiliate-tiers',
            meta: { permission: ['affiliate.manage'] },
            path: 'tiers',
            component: () => import('../pages/affiliate/TiersPage.vue'),
          },
          {
            name: 'affiliate-commissions',
            meta: { permission: ['affiliate.commissions.view'] },
            path: 'commissions',
            component: () => import('../pages/affiliate/CommissionsPage.vue'),
          },
          {
            name: 'affiliate-payouts',
            meta: { permission: ['affiliate.commissions.view'] },
            path: 'payouts',
            component: () => import('../pages/affiliate/PayoutsPage.vue'),
          },
          {
            name: 'affiliate-me',
            meta: { permission: ['affiliate.self'] },
            path: 'me',
            component: () => import('../pages/affiliate/MyAffiliatePage.vue'),
          },
        ],
      },
      {
        name: 'access',
        meta: { permission: ['access.manage'] },
        path: '/access',
        component: () => import('../pages/access/RolesPage.vue'),
      },
      {
        name: 'faq',
        path: '/faq',
        component: () => import('../pages/faq/FaqPage.vue'),
      },
    ],
  },
  {
    path: '/auth',
    component: AuthLayout,
    children: [
      {
        name: 'login',
        path: 'login',
        component: () => import('../pages/auth/Login.vue'),
      },
      {
        name: 'signup',
        path: 'signup',
        component: () => import('../pages/auth/Signup.vue'),
      },
      {
        name: 'recover-password',
        path: 'recover-password',
        component: () => import('../pages/auth/RecoverPassword.vue'),
      },
      {
        name: 'recover-password-email',
        path: 'recover-password-email',
        component: () => import('../pages/auth/CheckTheEmail.vue'),
      },
      {
        path: '',
        redirect: { name: 'login' },
      },
    ],
  },
  {
    name: '404',
    path: '/404',
    component: () => import('../pages/404.vue'),
  },
]

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior(to, from, savedPosition) {
    if (savedPosition) {
      return savedPosition
    }
    // For some reason using documentation example doesn't scroll on page navigation.
    if (to.hash) {
      return { el: to.hash, behavior: 'smooth' }
    } else {
      window.scrollTo(0, 0)
    }
  },
  routes,
})

router.beforeEach(async (to, from, next) => {
  const publicPages = ['/auth/login', '/auth/signup', '/auth/recover-password']
  const authRequired = !publicPages.includes(to.path) && !to.path.startsWith('/preview/post/')
  const loggedIn = localStorage.getItem('userToken')

  if (authRequired && !loggedIn) {
    // Login sends the user back to the page they asked for
    const query = to.fullPath === '/' || to.name === 'dashboard' ? {} : { redirect: to.fullPath }
    return next({ name: 'login', query })
  }

  if (loggedIn && authRequired) {
    // sessions from before permissions existed (or an edited role) load them once
    if (!hasPermissions()) {
      try {
        await refreshAccess()
      } catch {
        return next({ name: 'login', query: { redirect: to.fullPath } })
      }
    }
    const allows = (meta?: RouteMeta) => !meta?.permission?.length || can(...meta.permission)
    const home = HOME_ROUTES.find((name) => allows(router.getRoutes().find((r) => r.name === name)?.meta)) || 'settings'
    if (!to.matched.every((record) => allows(record.meta)) && to.name !== home) {
      return next({ name: home })
    }
  }

  next()
})
export default router
